/**
 * <s-header-menu> — desktop menu row beneath the logo bar that stays hidden until
 * the header's menu icon is pressed.
 *
 * The menu icon is a button with `commandfor` pointing at this element's id and
 * `command="--toggle"`. Each press toggles `data-open` on this element, which the
 * stylesheet uses to show the row, and keeps every invoking button's
 * `aria-expanded` in sync. Escape closes the row and returns focus to the button.
 */
class SHeaderMenu extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener(
      'command',
      (event) => {
        if (event.command !== '--toggle') return;
        this.#setOpen(!this.hasAttribute('data-open'));
      },
      opts
    );

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !this.hasAttribute('data-open')) return;
        if (event.defaultPrevented) return;
        this.#setOpen(false);
        const button = this.#buttons()[0];
        if (button instanceof HTMLElement) button.focus();
      },
      opts
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #buttons() {
    if (!this.id) return [];
    return Array.from(document.querySelectorAll(`button[commandfor="${this.id}"]`));
  }

  #setOpen(open) {
    this.toggleAttribute('data-open', open);
    for (const button of this.#buttons()) {
      button.setAttribute('aria-expanded', String(open));
    }
  }
}

customElements.define('s-header-menu', SHeaderMenu);
